import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaSearch, FaImage, FaSpinner, FaTimes } from 'react-icons/fa';
import menuApiService from '../services/menuApi';

const MenuManagement = () => {
  // Состояния для данных
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [stats, setStats] = useState({});
  
  // Состояния для UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('dishes'); // 'dishes' | 'categories' | 'modifiers'
  
  // Выбранное заведение
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  
  // Фильтры и поиск
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  
  // Пагинация
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Модальные окна
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add'); // 'add', 'edit', 'view', 'category'
  const [selectedDish, setSelectedDish] = useState(null);
  const [selectedModifier, setSelectedModifier] = useState(null);
  // Флаг удаления текущего изображения блюда при редактировании
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);
  
  // Формы
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    old_price: '',
    weight: '',
    calories: '',
    category: '',
    image: null,
    is_available: true,
    is_vegetarian: false,
    is_spicy: false,
    is_new: false,
    is_popular: false,
    order: 0
  });
  
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    description: '',
    establishment: '',
    is_active: true,
    order: 0,
    discount_percent: 0
  });

  const [modifierFormData, setModifierFormData] = useState({
    name: '',
    group_name: '',
    sku: '',
    default_quantity: 0,
    base_price: 0,
    is_active: true,
    order: 0,
    establishment: ''
  });

  // Привязка модификаторов к блюду (только для edit/add)
  const [dishModifierIds, setDishModifierIds] = useState([]);
  const [dishModifierQuery, setDishModifierQuery] = useState('');

  // Загрузка данных
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Явно тянем список заведений с корректного эндпоинта (fallback на старый)
      const token = localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' };
      let establishmentsResp;
      try {
        const r1 = await fetch('/api/establishments/list/', { headers });
        if (!r1.ok) throw new Error(String(r1.status));
        establishmentsResp = await r1.json();
      } catch (_) {
        try {
          // fallback на старый эндпоинт (требует авторизацию)
          const r2 = await fetch('/api/establishments/', { headers });
          if (!r2.ok) throw new Error(String(r2.status));
          establishmentsResp = await r2.json();
        } catch (_) {
          // последний фолбэк: основной домен (без авторизации)
          try {
            const r3 = await fetch('https://chuduhouse.ru/api/establishments/list/');
            if (!r3.ok) throw new Error(String(r3.status));
            establishmentsResp = await r3.json();
          } catch (e) {
            establishmentsResp = [];
          }
        }
      }
      const estList = Array.isArray(establishmentsResp)
        ? establishmentsResp
        : (establishmentsResp?.results || establishmentsResp?.establishments || establishmentsResp?.data || []);
      setEstablishments(estList);

      // Эффективное выбранное заведение (берём первое, если ещё не выбрано)
      const effectiveEst = selectedEstablishment || (estList.length > 0 ? estList[0] : null);
      if (effectiveEst && (!selectedEstablishment || selectedEstablishment.id !== effectiveEst.id)) {
        setSelectedEstablishment(effectiveEst);
      }

      // Если заведение определено, загружаем данные для него
      if (effectiveEst) {
        const dataPromises = [menuApiService.getMenuStats(effectiveEst.id)];

        // Загружаем данные в зависимости от активной вкладки
        if (activeTab === 'dishes') {
          dataPromises.push(
            menuApiService.getAdminDishes({
              establishment: effectiveEst.id,
              search: searchTerm,
              category: selectedCategory !== 'all' ? selectedCategory : undefined,
              is_available: availabilityFilter !== 'all' ? availabilityFilter : undefined,
              page: currentPage,
              page_size: 20
            })
          );
        }

        if (activeTab === 'modifiers') {
          dataPromises.push(
            menuApiService.getAdminModifiers({
              establishment: effectiveEst.id,
              search: searchTerm,
              page: currentPage,
              page_size: 50
            })
          );
        }
        
        dataPromises.push(menuApiService.getAdminCategories(effectiveEst.id));

        const dataResponses = await Promise.all(dataPromises);
        
        setStats(dataResponses[0] || {});
        
        if (activeTab === 'dishes') {
          const dishesResponse = dataResponses[1];
          setDishes(dishesResponse.results || []);
          setTotalPages(dishesResponse.total_pages || 1);
          setTotalCount(dishesResponse.count || 0);
        }

        if (activeTab === 'modifiers') {
          const modsResponse = dataResponses[1];
          setModifiers(modsResponse.results || []);
          setTotalPages(modsResponse.total_pages || 1);
          setTotalCount(modsResponse.count || 0);
        }
        
        const categoriesResponse = dataResponses[activeTab === 'dishes' || activeTab === 'modifiers' ? 2 : 1];
        let allCategories = categoriesResponse.results || [];
        
        // Фильтрация категорий по поиску
        if (activeTab === 'categories' && searchTerm) {
          allCategories = allCategories.filter(category =>
            category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }
        
        setCategories(allCategories);
      }
      
      setError(null);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки данных');
      console.error('Ошибка загрузки данных:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, availabilityFilter, currentPage, activeTab, selectedEstablishment]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Автоселект первого заведения при загрузке списка
  useEffect(() => {
    if (!selectedEstablishment && Array.isArray(establishments) && establishments.length > 0) {
      setSelectedEstablishment(establishments[0]);
    }
  }, [establishments, selectedEstablishment]);

  // Обработчики поиска и фильтрации
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Сбрасываем на первую страницу при поиске
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1);
  };

  const handleAvailabilityChange = (e) => {
    setAvailabilityFilter(e.target.value);
    setCurrentPage(1);
  };

  // Пагинация
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Модальные окна
  const openModal = (type, dish = null) => {
    setModalType(type);
    setSelectedDish(dish);
    setRemoveCurrentImage(false);
    setDishModifierQuery('');
    
    if (type === 'edit' && dish) {
      setFormData({
        name: dish.name || '',
        description: dish.description || '',
        price: dish.price || '',
        old_price: dish.old_price || '',
        weight: dish.weight || '',
        calories: dish.calories || '',
        category: dish.category || '',
        image: null,
        is_available: dish.is_available || true,
        is_vegetarian: dish.is_vegetarian || false,
        is_spicy: dish.is_spicy || false,
        is_new: dish.is_new || false,
        is_popular: dish.is_popular || false,
        order: dish.order || 0
      });
      // Подтянем справочник модификаторов (если ещё не тянули)
      if (!Array.isArray(modifiers) || modifiers.length === 0) {
        loadAllModifiersForEstablishment();
      }
    } else if (type === 'add') {
      setFormData({
        name: '',
        description: '',
        price: '',
        old_price: '',
        weight: '',
        calories: '',
        category: categories.length > 0 ? categories[0].id : '',
        image: null,
        is_available: true,
        is_vegetarian: false,
        is_spicy: false,
        is_new: false,
        is_popular: false,
        order: 0
      });
      setDishModifierIds([]);
      if (!Array.isArray(modifiers) || modifiers.length === 0) {
        loadAllModifiersForEstablishment();
      }
    } else if (type === 'category') {
      setCategoryFormData({
        name: '',
        description: '',
        establishment: selectedEstablishment ? selectedEstablishment.id : '',
        is_active: true,
        order: 0,
        discount_percent: 0
      });
    }
    
    setShowModal(true);
  };

  const openModifierModal = (type, modifier = null) => {
    setModalType(type); // 'modifier_add' | 'modifier_edit'
    setSelectedModifier(modifier);
    if (type === 'modifier_edit' && modifier) {
      setModifierFormData({
        name: modifier.name || '',
        group_name: modifier.group_name || '',
        sku: modifier.sku || '',
        default_quantity: modifier.default_quantity || 0,
        base_price: modifier.base_price || 0,
        is_active: modifier.is_active !== false,
        order: modifier.order || 0,
        establishment: modifier.establishment || (selectedEstablishment?.id || '')
      });
    } else {
      setModifierFormData({
        name: '',
        group_name: '',
        sku: '',
        default_quantity: 0,
        base_price: 0,
        is_active: true,
        order: 0,
        establishment: selectedEstablishment?.id || ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDish(null);
    setSelectedModifier(null);
    setError(null);
    setDishModifierQuery('');
  };

  // Подгружаем детали блюда при редактировании (нужны modifier_ids)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!showModal) return;
      if (modalType !== 'edit') return;
      if (!selectedDish?.id) return;
      try {
        const details = await menuApiService.getDishDetails(selectedDish.id);
        if (cancelled) return;
        const ids = Array.isArray(details?.modifier_ids) ? details.modifier_ids : [];
        setDishModifierIds(ids.map(x => String(x)));
      } catch (_) {
        // no-op
      }
    };
    run();
    return () => { cancelled = true; };
  }, [showModal, modalType, selectedDish?.id]);

  // Обработчики форм
  const handleFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCategoryFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setCategoryFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setCategoryFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleModifierFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setModifierFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setModifierFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const loadAllModifiersForEstablishment = async () => {
    if (!selectedEstablishment?.id) return;
    try {
      const resp = await menuApiService.getAdminModifiers({ establishment: selectedEstablishment.id, page: 1, page_size: 500 });
      setModifiers(resp.results || []);
    } catch (_) {}
  };

  // CRUD операции для блюд
  const handleSubmitDish = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      // Санитизация числовых полей: пустые строки не отправляем (иначе Django DecimalField падает)
      const sanitizeDishPayload = (src) => {
        const p = { ...src };
        const dropIfEmpty = (key) => {
          if (p[key] === '' || p[key] === null) delete p[key];
        };
        dropIfEmpty('old_price');
        dropIfEmpty('price'); // на всякий, хотя цена обязательна в UI
        dropIfEmpty('order');
        return p;
      };

      // Подмешиваем выбранные модификаторы (как CSV строка, чтобы FormData не ломалась)
      const payload = sanitizeDishPayload({
        ...formData,
        modifier_ids: Array.isArray(dishModifierIds) ? dishModifierIds.join(',') : ''
      });
      // Если пометили удаление изображения и не выбрано новое — отправим сигнал на удаление
      if (modalType === 'edit' && removeCurrentImage && !formData.image) {
        // Backend-совместимый вариант: и пустая строка, и специальный флаг
        payload.image = '';
        payload._remove_image = true;
      }

      if (modalType === 'add') {
        await menuApiService.createDish(payload);
      } else if (modalType === 'edit') {
        await menuApiService.updateDish(selectedDish.id, payload);
      }
      
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // CRUD операции для модификаторов (шаблонов)
  const handleSubmitModifier = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const payload = {
        ...modifierFormData,
        establishment: modifierFormData.establishment || (selectedEstablishment?.id || '')
      };
      if (modalType === 'modifier_add') {
        await menuApiService.createModifier(payload);
      } else if (modalType === 'modifier_edit') {
        await menuApiService.updateModifier(selectedModifier.id, payload);
      }
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteModifier = async (modifierId) => {
    if (!window.confirm('Удалить модификатор?')) return;
    setUpdating(true);
    try {
      await menuApiService.deleteModifier(modifierId);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это блюдо?')) {
      return;
    }
    
    setUpdating(true);
    try {
      await menuApiService.deleteDish(dishId);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // CRUD операции для категорий
  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await menuApiService.createCategory(categoryFormData);
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleEditCategory = (category) => {
    setSelectedDish(category); // Используем selectedDish для категории
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      establishment: category.establishment || '',
      is_active: category.is_active || true,
      order: category.order || 0,
      discount_percent: category.discount_percent || 0
    });
    setModalType('editCategory');
    setShowModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await menuApiService.updateCategory(selectedDish.id, categoryFormData);
      await loadData();
      closeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
      return;
    }
    
    setUpdating(true);
    try {
      await menuApiService.deleteCategory(categoryId);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleViewCategory = (category) => {
    setSelectedDish(category); // Используем selectedDish для категории
    setModalType('viewCategory');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-blue-500" />
        <span className="ml-2 text-lg">Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Заголовок и статистика */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <h1 className="text-3xl font-bold text-gray-800">Управление меню</h1>
          
          {/* Селектор заведения */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Заведение:</label>
            <select
              value={selectedEstablishment ? String(selectedEstablishment.id) : ''}
              onChange={(e) => {
                const establishment = (Array.isArray(establishments) ? establishments : [])
                  .find(est => String(est.id) === e.target.value);
                setSelectedEstablishment(establishment);
                setCurrentPage(1); // Сбрасываем пагинацию
                setSearchTerm(''); // Сбрасываем поиск
                setSelectedCategory('all'); // Сбрасываем фильтр категорий
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Выберите заведение</option>
              {Array.isArray(establishments) && establishments.map(establishment => (
                <option key={establishment.id} value={String(establishment.id)}>
                  {establishment.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Статистика */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-600">{stats.total_dishes || 0}</div>
            <div className="text-sm text-blue-500">Всего блюд</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-600">{stats.active_dishes || 0}</div>
            <div className="text-sm text-green-500">Активных блюд</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <div className="text-2xl font-bold text-orange-600">{stats.total_categories || 0}</div>
            <div className="text-sm text-orange-500">Категорий</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-600">{stats.inactive_dishes || 0}</div>
            <div className="text-sm text-purple-500">Неактивных блюд</div>
          </div>
        </div>

        {/* Вкладки */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => {
                setActiveTab('dishes');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'dishes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Блюда ({stats.total_dishes || 0})
            </button>
            <button
              onClick={() => {
                setActiveTab('categories');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Категории ({stats.total_categories || 0})
            </button>
            <button
              onClick={() => {
                setActiveTab('modifiers');
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'modifiers'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Модификаторы ({totalCount || 0})
            </button>
          </nav>
        </div>
      </div>

      {/* Ошибки */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Предупреждение о необходимости выбрать заведение */}
      {!selectedEstablishment && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
          Выберите заведение для управления меню
        </div>
      )}

      {/* Панель управления */}
      {selectedEstablishment && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Поиск */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder={
                    activeTab === 'dishes'
                      ? "Поиск блюд..."
                      : activeTab === 'categories'
                        ? "Поиск категорий..."
                        : "Поиск модификаторов..."
                  }
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Фильтры для блюд */}
            {activeTab === 'dishes' && (
              <div className="flex gap-4">
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Все категории</option>
                  {Array.isArray(categories) && categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>

                <select
                  value={availabilityFilter}
                  onChange={handleAvailabilityChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Все блюда</option>
                  <option value="true">Доступные</option>
                  <option value="false">Недоступные</option>
                </select>
              </div>
            )}

            {/* Кнопки действий */}
            <div className="flex gap-2">
              {activeTab === 'categories' && (
                <button
                  onClick={() => openModal('category')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Категория
                </button>
              )}
              {activeTab === 'dishes' && (
                <button
                  onClick={() => openModal('add')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Блюдо
                </button>
              )}
              {activeTab === 'modifiers' && (
                <button
                  onClick={() => openModifierModal('modifier_add')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <FaPlus /> Модификатор
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Информация о результатах */}
        <div className="px-4 py-2 bg-gray-50 text-sm text-gray-600">
          {activeTab === 'dishes'
            ? `Найдено ${totalCount} блюд • Страница ${currentPage} из ${totalPages}`
            : activeTab === 'categories'
              ? `Найдено ${categories.length} категорий`
              : `Найдено ${totalCount} модификаторов • Страница ${currentPage} из ${totalPages}`
          }
        </div>
      </div>
      )}

      {/* Контент вкладок */}
      {selectedEstablishment && (
        activeTab === 'dishes' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.isArray(dishes) && dishes.map((dish) => (
            <div key={dish.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
              {/* Изображение блюда */}
              <div className="relative h-48 bg-gray-100">
                {dish.image ? (
                  <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    src={dish.image}
                    alt={dish.name}
                    onError={(e) => {
                      e.target.src = '/api/placeholder/300/200';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FaImage className="text-gray-400 text-4xl" />
                  </div>
                )}
                
                {/* Статус доступности */}
                <div className="absolute top-3 right-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium shadow-sm ${
                    dish.is_available
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {dish.is_available ? 'Доступно' : 'Недоступно'}
                  </span>
                </div>

                {/* Бейджи особенностей */}
                <div className="absolute bottom-3 left-3 flex flex-wrap gap-1">
                  {dish.is_vegetarian && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                      🌱
                    </span>
                  )}
                  {dish.is_spicy && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                      🌶️
                    </span>
                  )}
                  {dish.is_new && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                      ✨
                    </span>
                  )}
                  {dish.is_popular && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                      🔥
                    </span>
                  )}
                </div>
              </div>

              {/* Информация о блюде */}
              <div className="p-4">
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{dish.name}</h3>
                  <p className="text-sm text-gray-500 mb-2 h-10 overflow-hidden">{dish.description}</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {dish.category_name}
                  </span>
                </div>

                {/* Цена и вес */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Если есть скидка категории, показываем финальную цену */}
                    {dish.category_discount > 0 ? (
                      <>
                        <span className="text-xl font-bold text-green-600">{dish.final_price} ₽</span>
                        <span className="text-sm text-gray-500 line-through">{dish.price} ₽</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          -{dish.category_discount}%
                        </span>
                      </>
                    ) : (
                      <>
                    <span className="text-xl font-bold text-gray-900">{dish.price} ₽</span>
                    {dish.old_price && (
                      <span className="text-sm text-gray-500 line-through">{dish.old_price} ₽</span>
                        )}
                      </>
                    )}
                  </div>
                  {dish.weight && (
                    <span className="text-sm text-gray-500">{dish.weight}</span>
                  )}
                </div>

                {/* Кнопки действий */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openModal('view', dish)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                    title="Просмотр"
                  >
                    <FaEye className="text-xs" />
                    Просмотр
                  </button>
                  <button
                    onClick={() => openModal('edit', dish)}
                    className="px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                    title="Редактировать"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteDish(dish.id)}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    title="Удалить"
                    disabled={updating}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        ) : activeTab === 'categories' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(categories) && categories.map((category) => (
            <div key={category.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300">
              {/* Информация о категории */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                  {/* Статус активности */}
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    category.is_active
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {category.is_active ? 'Активна' : 'Неактивна'}
                  </span>
                </div>
                
                {category.description && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{category.description}</p>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">{category.establishment_name}</span>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">{category.active_dishes_count}</span>
                    <span className="text-gray-400">/{category.dishes_count} блюд</span>
                  </div>
                </div>

                                 {/* Кнопки действий */}
                 <div className="flex items-center gap-2">
                   <button
                     onClick={() => handleViewCategory(category)}
                     className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1"
                   >
                     <FaEye className="text-xs" />
                     Просмотр
                   </button>
                   <button
                     onClick={() => handleEditCategory(category)}
                     className="px-3 py-2 text-sm font-medium text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
                     title="Редактировать"
                   >
                     <FaEdit />
                   </button>
                   <button
                     onClick={() => handleDeleteCategory(category.id)}
                     className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                     title="Удалить"
                     disabled={updating}
                   >
                     <FaTrash />
                   </button>
                 </div>
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Группа</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Название</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Цена</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">По умолчанию</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Активен</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(modifiers) && modifiers.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700">{m.group_name || '—'}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{m.base_price} ₽</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{m.default_quantity || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          m.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {m.is_active ? 'Да' : 'Нет'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-2">
                          <button
                            onClick={() => openModifierModal('modifier_edit', m)}
                            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Редактировать"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteModifier(m.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Удалить"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(!Array.isArray(modifiers) || modifiers.length === 0) && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                        Нет модификаторов
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
      )
      )}

      {/* Пагинация для блюд */}
      {selectedEstablishment && activeTab === 'dishes' && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6 overflow-hidden">
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Предыдущая
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Следующая
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Показано <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> до{' '}
                    <span className="font-medium">{Math.min(currentPage * 20, totalCount)}</span> из{' '}
                    <span className="font-medium">{totalCount}</span> результатов
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Предыдущая
                    </button>
                    
                    {/* Номера страниц */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Следующая
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальные окна */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              {/* Заголовок модального окна */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === 'add' && 'Добавить блюдо'}
                  {modalType === 'edit' && 'Редактировать блюдо'}
                  {modalType === 'view' && 'Просмотр блюда'}
                  {modalType === 'category' && 'Добавить категорию'}
                  {modalType === 'editCategory' && 'Редактировать категорию'}
                  {modalType === 'viewCategory' && 'Просмотр категории'}
                  {modalType === 'modifier_add' && 'Добавить модификатор'}
                  {modalType === 'modifier_edit' && 'Редактировать модификатор'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Содержимое модального окна */}
              {modalType === 'viewCategory' && selectedDish && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {selectedDish.image ? (
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/300/200';
                          }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FaImage className="text-gray-400 text-4xl" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Название</label>
                        <p className="text-sm text-gray-900">{selectedDish.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Заведение</label>
                        <p className="text-sm text-gray-900">{selectedDish.establishment_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Количество блюд</label>
                        <p className="text-sm text-gray-900">
                          {selectedDish.active_dishes_count} активных / {selectedDish.dishes_count} всего
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Порядок</label>
                        <p className="text-sm text-gray-900">{selectedDish.order}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Скидка</label>
                        <p className="text-sm text-gray-900">
                          {selectedDish.discount_percent ? `${selectedDish.discount_percent}%` : 'Нет скидки'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Статус</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedDish.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedDish.is_active ? 'Активна' : 'Неактивна'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedDish.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Описание</label>
                      <p className="text-sm text-gray-900">{selectedDish.description}</p>
                    </div>
                  )}
                </div>
              )}

              {modalType === 'view' && selectedDish && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      {selectedDish.image ? (
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="w-full h-48 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/300/200';
                          }}
                        />
                      ) : (
                        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FaImage className="text-gray-400 text-4xl" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Название</label>
                        <p className="text-sm text-gray-900">{selectedDish.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Категория</label>
                        <p className="text-sm text-gray-900">{selectedDish.category_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Цена</label>
                        <p className="text-sm text-gray-900">
                          {selectedDish.category_discount > 0 ? (
                            <>
                              <span className="text-lg font-bold text-green-600">{selectedDish.final_price} ₽</span>
                              <span className="ml-2 text-gray-500 line-through">{selectedDish.price} ₽</span>
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                -{selectedDish.category_discount}%
                              </span>
                            </>
                          ) : (
                            <>
                          {selectedDish.price} ₽
                          {selectedDish.old_price && (
                            <span className="ml-2 text-gray-500 line-through">
                              {selectedDish.old_price} ₽
                            </span>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                      {selectedDish.weight && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Вес/Объем</label>
                          <p className="text-sm text-gray-900">{selectedDish.weight}</p>
                        </div>
                      )}
                      {selectedDish.calories && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Калорийность</label>
                          <p className="text-sm text-gray-900">{selectedDish.calories}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Статус</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          selectedDish.is_available
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedDish.is_available ? 'Доступно' : 'Недоступно'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedDish.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Описание</label>
                      <p className="text-sm text-gray-900">{selectedDish.description}</p>
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {selectedDish.is_vegetarian && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        🌱 Вегетарианское
                      </span>
                    )}
                    {selectedDish.is_spicy && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        🌶️ Острое
                      </span>
                    )}
                    {selectedDish.is_new && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        ✨ Новинка
                      </span>
                    )}
                    {selectedDish.is_popular && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        🔥 Популярное
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Форма блюда */}
              {(modalType === 'add' || modalType === 'edit') && (
                <form onSubmit={handleSubmitDish} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Категория *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите категорию</option>
                        {Array.isArray(categories) && categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Модификаторы блюда (шаблоны) */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">
                        Модификаторы блюда
                      </label>
                      <button
                        type="button"
                        onClick={loadAllModifiersForEstablishment}
                        className="text-xs text-blue-600 hover:text-blue-800"
                        title="Подгрузить справочник модификаторов"
                      >
                        Обновить список
                      </button>
                    </div>
                    <div className="mt-2">
                      <input
                        type="text"
                        value={dishModifierQuery}
                        onChange={(e) => setDishModifierQuery(e.target.value)}
                        placeholder="Поиск модификаторов…"
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        Выбрано: <strong>{dishModifierIds.length}</strong>
                      </div>
                    </div>

                    <div className="mt-2 border border-gray-200 rounded-md p-2 max-h-[260px] overflow-auto space-y-2">
                      {(() => {
                        const grouped = (Array.isArray(modifiers) ? modifiers : [])
                          .filter(m => {
                            const q = (dishModifierQuery || '').toLowerCase().trim();
                            if (!q) return true;
                            const hay = `${m.group_name || ''} ${m.name || ''} ${m.sku || ''}`.toLowerCase();
                            return hay.includes(q);
                          })
                          .reduce((acc, m) => {
                            const g = (m.group_name || 'Без группы').trim() || 'Без группы';
                            if (!acc[g]) acc[g] = [];
                            acc[g].push(m);
                            return acc;
                          }, {});

                        const groupNames = Object.keys(grouped);
                        if (groupNames.length === 0) {
                          return (
                            <div className="text-sm text-gray-500 py-6 text-center">
                              Нет модификаторов по фильтру
                            </div>
                          );
                        }

                        return groupNames.sort().map(g => (
                          <div key={g}>
                            <div className="text-xs font-semibold text-gray-600 mb-1">{g}</div>
                            <div className="flex flex-wrap gap-2">
                              {grouped[g].map(m => {
                                const idStr = String(m.id);
                                const checked = dishModifierIds.includes(idStr);
                                return (
                                  <button
                                    key={m.id}
                                    type="button"
                                    onClick={() => {
                                      setDishModifierIds(prev => {
                                        const set = new Set(prev);
                                        if (set.has(idStr)) set.delete(idStr);
                                        else set.add(idStr);
                                        return Array.from(set);
                                      });
                                    }}
                                    className={`px-3 py-2 rounded-full text-sm border transition-colors ${
                                      checked
                                        ? 'bg-blue-50 border-blue-300 text-blue-800'
                                        : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
                                    }`}
                                    title={m.sku ? `Артикул: ${m.sku}` : ''}
                                  >
                                    {(m.name || '').trim()} {Number(m.base_price || 0) > 0 ? `(+${m.base_price}₽)` : ''}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    {(!Array.isArray(modifiers) || modifiers.length === 0) && (
                      <div className="mt-2 text-xs text-gray-500">
                        Список модификаторов пуст. Нажми «Обновить список» (или зайди во вкладку «Модификаторы»).
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Цена *
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleFormChange}
                        required
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Старая цена
                      </label>
                      <input
                        type="number"
                        name="old_price"
                        value={formData.old_price}
                        onChange={handleFormChange}
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Вес/Объем
                      </label>
                      <input
                        type="text"
                        name="weight"
                        value={formData.weight}
                        onChange={handleFormChange}
                        placeholder="например: 250г"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Калорийность
                      </label>
                      <input
                        type="text"
                        name="calories"
                        value={formData.calories}
                        onChange={handleFormChange}
                        placeholder="например: 350 ккал"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Порядок сортировки
                      </label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleFormChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Изображение
                    </label>
                    {/* Превью существующего изображения (если есть) */}
                    {modalType !== 'add' && selectedDish?.image && !removeCurrentImage && (
                      <div className="mt-2 mb-3 relative inline-block">
                        <img
                          src={selectedDish.image}
                          alt={selectedDish.name}
                          className="h-24 w-24 object-cover rounded-lg border"
                          onError={(e) => { e.target.src = '/api/placeholder/150/150'; }}
                        />
                        <button
                          type="button"
                          onClick={() => setRemoveCurrentImage(true)}
                          title="Удалить изображение"
                          className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center shadow-sm hover:bg-gray-50"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                    {modalType !== 'add' && selectedDish?.images && Array.isArray(selectedDish.images) && selectedDish.images.length > 0 && (
                      <div className="mt-2 mb-3 flex flex-wrap gap-2">
                        {selectedDish.images.map((img, idx) => (
                          <div key={idx} className="relative inline-block">
                            <img
                              src={img}
                              alt={`${selectedDish.name}-${idx}`}
                              className="h-20 w-20 object-cover rounded-md border"
                              onError={(e) => { e.target.src = '/api/placeholder/120/120'; }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      type="file"
                      name="image"
                      onChange={handleFormChange}
                      accept="image/*"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {removeCurrentImage && (
                      <div className="mt-2 text-xs text-red-600">
                        Текущее изображение будет удалено при сохранении.
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_available"
                        checked={formData.is_available}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Доступно для заказа
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_vegetarian"
                        checked={formData.is_vegetarian}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🌱 Вегетарианское
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_spicy"
                        checked={formData.is_spicy}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🌶️ Острое
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_new"
                        checked={formData.is_new}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        ✨ Новинка
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_popular"
                        checked={formData.is_popular}
                        onChange={handleFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        🔥 Популярное
                      </label>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      {modalType === 'add' ? 'Создать' : 'Сохранить'}
                    </button>
                  </div>
                </form>
              )}

              {/* Форма модификатора */}
              {(modalType === 'modifier_add' || modalType === 'modifier_edit') && (
                <form onSubmit={handleSubmitModifier} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Название *</label>
                      <input
                        type="text"
                        name="name"
                        value={modifierFormData.name}
                        onChange={handleModifierFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Группа</label>
                      <input
                        type="text"
                        name="group_name"
                        value={modifierFormData.group_name}
                        onChange={handleModifierFormChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Цена (₽)</label>
                      <input
                        type="number"
                        name="base_price"
                        value={modifierFormData.base_price}
                        onChange={handleModifierFormChange}
                        min="0"
                        step="0.01"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Кол-во по умолчанию</label>
                      <input
                        type="number"
                        name="default_quantity"
                        value={modifierFormData.default_quantity}
                        onChange={handleModifierFormChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Порядок</label>
                      <input
                        type="number"
                        name="order"
                        value={modifierFormData.order}
                        onChange={handleModifierFormChange}
                        min="0"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Артикул</label>
                      <input
                        type="text"
                        name="sku"
                        value={modifierFormData.sku}
                        onChange={handleModifierFormChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="flex items-center mt-6">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={modifierFormData.is_active}
                        onChange={handleModifierFormChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Активен
                      </label>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      Сохранить
                    </button>
                  </div>
                </form>
              )}

              {/* Форма редактирования категории */}
              {modalType === 'editCategory' && (
                <form onSubmit={handleUpdateCategory} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={categoryFormData.name}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Заведение *
                      </label>
                      <select
                        name="establishment"
                        value={categoryFormData.establishment}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите заведение</option>
                        {Array.isArray(establishments) && establishments.map(establishment => (
                          <option key={establishment.id} value={establishment.id}>
                            {establishment.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Порядок сортировки
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={categoryFormData.order}
                      onChange={handleCategoryFormChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Скидка на категорию (%)
                      </label>
                      <input
                        type="number"
                        name="discount_percent"
                        value={categoryFormData.discount_percent || 0}
                        onChange={handleCategoryFormChange}
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0.00"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Все блюда в категории станут дешевле на указанный процент
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={categoryFormData.is_active}
                      onChange={handleCategoryFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Активная категория
                    </label>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      Сохранить изменения
                    </button>
                  </div>
                </form>
              )}

              {/* Форма категории */}
              {modalType === 'category' && (
                <form onSubmit={handleSubmitCategory} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Название *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={categoryFormData.name}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Заведение *
                      </label>
                      <select
                        name="establishment"
                        value={categoryFormData.establishment}
                        onChange={handleCategoryFormChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Выберите заведение</option>
                        {Array.isArray(establishments) && establishments.map(establishment => (
                          <option key={establishment.id} value={establishment.id}>
                            {establishment.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Описание
                    </label>
                    <textarea
                      name="description"
                      value={categoryFormData.description}
                      onChange={handleCategoryFormChange}
                      rows={3}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Порядок сортировки
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={categoryFormData.order}
                      onChange={handleCategoryFormChange}
                      min="0"
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Скидка на категорию (%)
                      </label>
                      <input
                        type="number"
                        name="discount_percent"
                        value={categoryFormData.discount_percent || 0}
                        onChange={handleCategoryFormChange}
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="0.00"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Все блюда в категории станут дешевле на указанный процент
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={categoryFormData.is_active}
                      onChange={handleCategoryFormChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Активная категория
                    </label>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating && <FaSpinner className="animate-spin" />}
                      Создать категорию
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuManagement; 